import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { moduleLabel, actionLabel } from "@/config/permissions";

// Generic permission grid. `value` is a Set of "module:action" strings.
// onToggle(perm, nextChecked) is called for each cell change.
// `baseline` (optional Set) marks permissions inherited from a role — shown with a dot.
export function PermissionMatrix({ modules, actions, value, baseline, onToggle, disabled }) {
  const has = (p) => value.has(p);
  const isBase = (p) => baseline && baseline.has(p);

  const rowPerms = (m) => actions.map((a) => `${m}:${a}`);
  const rowAll = (m) => rowPerms(m).every((p) => has(p));
  const toggleRow = (m) => {
    const next = !rowAll(m);
    rowPerms(m).forEach((p) => { if (has(p) !== next) onToggle(p, next); });
  };

  return (
    <div className="overflow-x-auto rounded-sm border border-border" data-testid="permission-matrix">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/60">
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Module</th>
            {actions.map((a) => (
              <th key={a} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{actionLabel(a)}</th>
            ))}
            <th className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">All</th>
          </tr>
        </thead>
        <tbody>
          {modules.map((m) => (
            <tr key={m} className="border-b border-border last:border-0 hover:bg-secondary/30">
              <td className="px-3 py-2 font-medium">{moduleLabel(m)}</td>
              {actions.map((a) => {
                const p = `${m}:${a}`;
                return (
                  <td key={a} className="px-2 py-2 text-center">
                    <div className="relative inline-flex items-center justify-center">
                      <Checkbox
                        data-testid={`perm-${p}`}
                        checked={has(p)}
                        disabled={disabled}
                        onCheckedChange={(v) => onToggle(p, !!v)}
                      />
                      {isBase(p) && (
                        <span className={cn("absolute -right-2 -top-1 h-1.5 w-1.5 rounded-full", has(p) ? "bg-primary" : "bg-muted-foreground/40")} title="From role" />
                      )}
                    </div>
                  </td>
                );
              })}
              <td className="px-2 py-2 text-center">
                <Checkbox checked={rowAll(m)} disabled={disabled} onCheckedChange={() => toggleRow(m)} data-testid={`perm-row-all-${m}`} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
